import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { ApiHost } from "../../utils/ApiConfig";
import ConfirmPopup from "./ConfirmPopup";
import { InputTypeConstants } from "../../constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import pl from "date-fns/locale/pl";


import logger from "../../utils/logger";
registerLocale("pl", pl);

/**
 * Komponent DataTable wyświetlający dane w tabeli z opcją sortowania,
 * filtrowania (globalnego dla pól tekstowych i indywidualnego dla checkboxów oraz dat)
 * oraz paginacji. Obsługuje też akcje na wierszach (np. przyciski operacji).
 */
const DataTable = ({
  data,
  itemsPerPage,
  columnHeaders,
  dataColumns,
  initialSortColumn,
  actionNames,
  actionHandlers,
  customFilters = null,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [sortColumn, setSortColumn] = useState(
    initialSortColumn || columnHeaders[0]
  );
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortedData, setSortedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [filterText, setFilterText] = useState("");
  const [filterColumn, setFilterColumn] = useState(columnHeaders[0]);

  const textFilters = useMemo(
    () =>
      customFilters
        ? customFilters.filter((f) => f.type === InputTypeConstants.TEXT)
        : [],
    [customFilters]
  );

  const otherFilters = useMemo(
    () =>
      customFilters
        ? customFilters.filter((f) => f.type !== InputTypeConstants.TEXT)
        : [],
    [customFilters]
  );

  const [filterValues, setFilterValues] = useState(() => {
    const initial = {};
    if (customFilters) {
      customFilters
        .filter((f) => f.type !== InputTypeConstants.TEXT)
        .forEach((f) => {
          switch (f.type) {
            case InputTypeConstants.CHECKBOX:
              initial[f.name] = false;
              break;
            case InputTypeConstants.DATE:
              initial[`${f.name}From`] = "";
              initial[`${f.name}To`] = "";
              break;
            default:
              initial[f.name] = "";
          }
        });
    }
    return initial;
  });

  const { accessToken } = useAuth();
  const { userId } = useAuth();
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [businessId, setBusinessId] = useState(null);

  /**
   * Usuwa powiązanie użytkownika z firmą.
   */
  const unlinkBusiness = async (bizId) => {
    try {
      await axios.post(
        `${ApiHost}/business/unlink`,
        { user_id: userId, business_id: bizId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
    } catch (error) {
      logger.error("Błąd usuwania powiązania z firmą:", error);
    }
  };

  /**
   * Zmienia numer aktualnej strony.
   */
  const paginate = (page) => setCurrentPage(page);

  /**
   * Ustawia kolumnę sortowania i porządek (asc/desc).
   */
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  /**
   * Przygotowuje wewnętrzną strukturę wierszy na podstawie danych
   */
  useEffect(() => {
    if (!data) return;
    const prepared = data.map((row) => ({
      ...columnHeaders.reduce((acc, header, idx) => {
        acc[header] = row[dataColumns[idx]];
        return acc;
      }, {}),
      _id: row.Id,
      Operations: row.Operations,
    }));
    setTableData(prepared);
  }, [data, dataColumns, columnHeaders]);

  /**
   * Efekt sortowania tabeli przy zmianie kolumny lub porządku.
   */
  useEffect(() => {
    let sorted = [...tableData];
    if (sortColumn) {
      sorted.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) return sortOrder === "asc" ? -1 : 1;
        if (a[sortColumn] > b[sortColumn]) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    setSortedData(sorted);
  }, [sortColumn, sortOrder, tableData]);

  /**
   * Efekt filtrowania
   */
  const computedFilteredData = useMemo(() => {
    let rows = sortedData;

    if (textFilters.length > 0) {
      rows = rows.filter((row) => {
        if (!filterText) return true;
        const cell = row[filterColumn];
        return String(cell).toLowerCase().includes(filterText.toLowerCase());
      });
    }

    if (otherFilters.length > 0) {
      rows = rows.filter((row) =>
        otherFilters.every((f) => {
          switch (f.type) {
            case InputTypeConstants.CHECKBOX:
              const checkboxVal = filterValues[f.name];
              if (!checkboxVal) return true;
              const cell = row[f.name];
              return Boolean(cell) === checkboxVal;

            case InputTypeConstants.DATETIME:
              const datetimeVal = filterValues[f.name];
              if (!datetimeVal) return true;
              const datetimeCell = row[f.name];
              return String(datetimeCell).startsWith(datetimeVal);

            case InputTypeConstants.DATE: {
              const from = filterValues[`${f.name}From`];
              const to = filterValues[`${f.name}To`];

              if (!from && !to) return true;

              const raw = row[f.name];
              if (!raw) {
                return true;
              }

              const [datePart] = raw.split(" ");
              const [y, m, d] = datePart.split("-");
              const cellDate = new Date(+y, +m - 1, +d, 0, 0, 0);

              if (from) {
                const [fy, fm, fd] = from.split("-");
                const fromDate = new Date(+fy, +fm - 1, +fd, 0, 0, 0);
                if (cellDate < fromDate) return false;
              }
              if (to) {
                const [ty, tm, td] = to.split("-");
                const toDate = new Date(+ty, +tm - 1, +td, 23, 59, 59, 999);
                if (cellDate > toDate) return false;
              }
              return true;
            }
            default:
              return true;
          }
        })
      );
    }
    return rows;
  }, [
    sortedData,
    filterText,
    filterColumn,
    filterValues,
    textFilters,
    otherFilters,
  ]);

  useEffect(() => {
    setFilteredData(computedFilteredData);
  }, [computedFilteredData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, filterColumn, filterValues]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  return (
    <div>
      {customFilters ? (
        <div className="d-flex flex-wrap mb-3 gap-2 align-items-center">
          {textFilters.length > 0 && (
            <>
              <select
                className="form-select w-auto me-2"
                value={filterColumn}
                onChange={(e) => setFilterColumn(e.target.value)}
              >
                {textFilters.map((f, i) => (
                  <option key={i} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-control w-auto me-3"
                placeholder="Wpisz tekst do filtrowania"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </>
          )}
          {otherFilters.map((f, idx) => {
            const val = filterValues[f.name];
            if (f.type === InputTypeConstants.CHECKBOX) {
              return (
                <div className="form-check me-3" key={idx}>
                  <input
                    id={`chk_${f.name}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={val}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [f.name]: e.target.checked,
                      }))
                    }
                  />
                  <label htmlFor={`chk_${f.name}`} className="form-check-label">
                    {f.name}
                  </label>
                </div>
              );
            } else if (f.type === InputTypeConstants.DATETIME) {
              return (
                <input
                  key={idx}
                  type="datetime-local"
                  className="form-control w-auto me-3"
                  value={val}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [f.name]: e.target.value,
                    }))
                  }
                  placeholder={f.name}
                />
              );
            } else if (f.type === InputTypeConstants.DATE) {
              const fromValue = filterValues[`${f.name}From`];
              const toValue = filterValues[`${f.name}To`];

              return (
                <div key={idx} className="d-flex align-items-center">
                  <label className="me-1">{f.name}</label>
                  <DatePicker
                    selected={fromValue ? new Date(fromValue) : null}
                    onChange={(date) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [`${f.name}From`]: date
                          ? date.toISOString().split("T")[0]
                          : "",
                      }))
                    }
                    selectsStart
                    startDate={fromValue ? new Date(fromValue) : null}
                    endDate={toValue ? new Date(toValue) : null}
                    placeholderText="Od"
                    locale="pl"
                    dateFormat="yyyy-MM-dd"
                    className="form-control w-auto"
                  />
                  <i className="bi bi-arrow-right ms-1 me-1"></i>
                  <DatePicker
                    selected={toValue ? new Date(toValue) : null}
                    onChange={(date) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [`${f.name}To`]: date
                          ? date.toISOString().split("T")[0]
                          : "",
                      }))
                    }
                    selectsEnd
                    startDate={fromValue ? new Date(fromValue) : null}
                    endDate={toValue ? new Date(toValue) : null}
                    placeholderText="Do"
                    locale="pl"
                    dateFormat="yyyy-MM-dd"
                    className="form-control rounded-start-0 border-start-0 w-auto"
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      ) : (
        <div className="d-flex mb-3">
          <select
            className="form-select w-auto me-2"
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
          >
            {columnHeaders.map((h, i) => (
              <option key={i} value={h}>
                {h}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="form-control w-auto"
            placeholder="Wpisz tekst do filtrowania"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      )}
      <table className="table">
        <thead>
          <tr>
            {columnHeaders.map((header, idx) => (
              <th
                key={idx}
                onClick={() => handleSort(header)}
                style={{ cursor: "pointer" }}
              >
                {header}
                {sortColumn === header &&
                  (sortOrder === "asc" ? (
                    <i className="bi bi-arrow-up"></i>
                  ) : (
                    <i className="bi bi-arrow-down"></i>
                  ))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentItems.map((row, rIdx) => (
            <tr key={rIdx}>
              {columnHeaders.map((header, cIdx) => (
                <td key={cIdx}>
                  {actionNames && header === "Operacje" ? (
                    <div>
                      {actionNames.map(
                        (name, aIdx) =>
                          row.Operations[aIdx] > 0 && (
                            <button
                              key={aIdx}
                              className={`btn btn-outline-primary${
                                aIdx > 0 ? " ms-1" : ""
                              }`}
                              onClick={() => actionHandlers[aIdx](row._id)}
                              disabled={row.Operations[aIdx] !== 2}
                            >
                              {name}
                            </button>
                          )
                      )}
                    </div>
                  ) : !actionNames && header === "Operacje" ? (
                    <div className="d-inline-block">
                      <button
                        className="btn btn-outline-primary w5 h2 tooltip"
                        onClick={() => {
                          setBusinessId(row[header]);
                          setShowConfirmPopup(true);
                        }}
                      >
                        Usuń
                        <span className="tooltiptext">
                          Usuń powiązanie z firmą
                        </span>
                      </button>
                    </div>
                  ) : typeof row[header] === "boolean" ? (
                    <span
                      className={`d-flex justify-content-center fs-4 ${
                        row[header]
                          ? "bi-check-circle-fill text-success"
                          : "bi-circle text-danger"
                      }`}
                      title={row[header] ? "Aktywny" : "Zablokowany"}
                    />
                  ) : (
                    row[header]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmPopup
        show={showConfirmPopup}
        onCancel={() => setShowConfirmPopup(false)}
        onConfirm={() => {
          setShowConfirmPopup(false);
          unlinkBusiness(businessId);
        }}
        title="Usuwanie powiązania z firmą"
        text="Czy na pewno chcesz usunąć powiązanie z firmą?"
        confirmText="Tak"
        cancelText="Nie"
      />
      <div className="d-flex flex-row">
        <nav className="d-flex justify-content-between align-items-center">
          <ul className="pagination mb-0">
            {(() => {
              const totalPages = Math.ceil(filteredData.length / itemsPerPage);
              const pages = [];
              const maxVis = 5;
              let start = Math.max(1, currentPage - Math.floor(maxVis / 2));
              let end = Math.min(totalPages, start + maxVis - 1);
              if (start > 1)
                pages.push(
                  <li
                    key={1}
                    className={`page-item ${currentPage === 1 ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => paginate(1)}>
                      1
                    </button>
                  </li>
                );
              if (start > 2)
                pages.push(
                  <li key="start-ellipsis" className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                );
              for (let i = start; i <= end; i++)
                pages.push(
                  <li
                    key={i}
                    className={`page-item ${currentPage === i ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => paginate(i)}>
                      {i}
                    </button>
                  </li>
                );
              if (end < totalPages - 1)
                pages.push(
                  <li key="end-ellipsis" className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                );
              if (end < totalPages)
                pages.push(
                  <li
                    key={totalPages}
                    className={`page-item ${
                      currentPage === totalPages ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </li>
                );
              return pages;
            })()}
          </ul>
        </nav>
        <span className="text-muted ms-auto">
          Wyświetlane obiekty: {indexOfFirstItem + 1}-
          {Math.min(indexOfLastItem, filteredData.length)} z{" "}
          {filteredData.length}
        </span>
      </div>
    </div>
  );
};

export default DataTable;


